import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { AlertTypes } from '../../../types';
import { Team } from '../../../types/teams.types';
import { Tag } from '../../../types/tags.types';
import { Group } from '../../../types/groups.types';
import { UnifiedFilterBar, FilterField } from '../../shared/UnifiedFilterBar';
import { useEffectiveSettings, SETTING } from '../../../hooks/use-effective-settings';
import { ISO_DATE_RE } from '../../../utils/dates';
import { displayName } from '../../../helpers/displayName';
import { ALERT_TYPE_LABELS } from '../../../helpers/alertTypeLabels';

interface Props {
    onFilter(filters: AlertTypes.Filters): Promise<void>;
    onDownload(): Promise<void>;
    allTeams: Team[];
    allTags: Tag[];
    allGroups: Group[];
}

const ALERT_STATUSES = [
    { label: 'Active', value: 'active' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Reviewed', value: 'reviewed' },
    { label: 'Pending Review', value: 'pending-review' },
    { label: 'Escalated', value: 'escalated' },
    { label: 'Expired', value: 'expired' },
];

const ALERT_TYPE_VALUES = ['SOS', 'CheckOnMe', 'FakeCall', 'RiskReview', 'Shift'];
const ALERT_TYPES = ALERT_TYPE_VALUES.map((value) => ({ label: ALERT_TYPE_LABELS[value], value }));

const ALERT_STATUS_LABELS: Record<string, string> = Object.fromEntries(
    ALERT_STATUSES.map((o) => [o.value, o.label]),
);
const TIME_PERIOD_LABELS: Record<string, string> = {
    '7d': 'Last 7 Days',
    '14d': 'Last 14 Days',
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days',
};

const VALID_ALERT_TYPES = new Set(ALERT_TYPES.map((o) => o.value));
const VALID_ALERT_STATUSES = new Set(ALERT_STATUSES.map((o) => o.value));

const VALID_TIME_PERIODS = new Set(['', '7d', '14d', '30d', '90d', 'custom']);

// Convert a non-custom time period to a {fromDate, toDate} pair anchored to today.
function periodToRange(period: string): { fromDate: string; toDate: string } | null {
    const days = period === '7d' ? 7 : period === '14d' ? 14 : period === '30d' ? 30 : period === '90d' ? 90 : null;
    if (days === null) return null;
    return {
        fromDate: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
        toDate: dayjs().format('YYYY-MM-DD'),
    };
}

// Reverse: given a (fromDate, toDate) pair, return the matching preset id or
// 'custom'. Match is exact — toDate must equal today and the day-diff must
// equal one of {7, 14, 30, 90}. Anything else (including a deep-link shared
// near a midnight boundary) safely falls through to 'custom', which still
// pre-populates the date pickers with the literal range from the URL.
function rangeToPeriod(fromDate: string, toDate: string): string {
    const today = dayjs().format('YYYY-MM-DD');
    if (toDate !== today) return 'custom';
    const days = dayjs(toDate).diff(dayjs(fromDate), 'day');
    if (days === 7) return '7d';
    if (days === 14) return '14d';
    if (days === 30) return '30d';
    if (days === 90) return '90d';
    return 'custom';
}

interface PeriodState {
    period: string;
    start: Dayjs | null;
    end: Dayjs | null;
}

const EMPTY_PERIOD: PeriodState = { period: '', start: null, end: null };

// Resolve the active range (preset or custom) into fromDate/toDate strings.
// Returns null when a custom range is incomplete (no dates picked yet) or
// invalid; callers can then either skip the date filter or surface a
// validation toast (handleApply does the latter).
function resolveDateRange(p: PeriodState): { fromDate: string; toDate: string } | null {
    if (p.period !== 'custom') return periodToRange(p.period);
    if (!p.start || !p.end) return null;
    if (!p.start.isValid() || !p.end.isValid()) return null;
    return {
        fromDate: p.start.format('YYYY-MM-DD'),
        toDate: p.end.format('YYYY-MM-DD'),
    };
}

type Values = {
    initiatedBy: string;
    status: string;
    alertType: string;
    groupIds: string[];
    teamIds: string[];
    tags: string[];
    // '' = server default (hide test data); 'show' / 'only' opt in.
    testData: '' | 'show' | 'only';
};

const EMPTY_VALUES: Values = {
    initiatedBy: '', status: '', alertType: '',
    groupIds: [], teamIds: [], tags: [], testData: '',
};

const TEST_DATA_OPTIONS = [
    { label: 'Hide test data', value: '' },
    { label: 'Show test data', value: 'show' },
    { label: 'Only test data', value: 'only' },
];
const TEST_DATA_LABELS: Record<string, string> = {
    show: 'Shown', only: 'Only test data',
};

export const AlertListFilters: React.FC<Props> = ({ allGroups, allTeams, allTags, onFilter, onDownload }) => {
    const [searchParams] = useSearchParams();
    const { getSetting } = useEffectiveSettings();
    const showTestMode = getSetting(SETTING.showTestMode, false);

    // Build initial values from URL params (for deep-link from dashboard).
    // Each param is allowlist-validated so a crafted URL can't inject arbitrary
    // strings into the filter state / outbound API call.
    const buildInitialValues = (): Values => {
        const v: Values = { ...EMPTY_VALUES };
        const urlAlertType = searchParams.get('alertType');
        const urlStatus = searchParams.get('status');
        if (urlAlertType && VALID_ALERT_TYPES.has(urlAlertType)) v.alertType = urlAlertType;
        if (urlStatus && VALID_ALERT_STATUSES.has(urlStatus)) v.status = urlStatus;
        return v;
    };

    const buildInitialPeriod = (): PeriodState => {
        const urlFrom = searchParams.get('fromDate');
        const urlTo = searchParams.get('toDate');
        if (urlFrom && urlTo && ISO_DATE_RE.test(urlFrom) && ISO_DATE_RE.test(urlTo)) {
            return {
                period: rangeToPeriod(urlFrom, urlTo),
                start: dayjs(urlFrom),
                end: dayjs(urlTo),
            };
        }
        return EMPTY_PERIOD;
    };

    const [values, setValues] = useState<Values>(buildInitialValues);
    // `values`/`periodState` reflect input state; `applied`/`appliedPeriod`
    // are post-Apply snapshots that drive the pills and stay stable while
    // the user types.
    const [applied, setApplied] = useState<Values>(buildInitialValues);

    // Time-period state. One useState holding the whole period object so the
    // builder runs only once (lazy init) — passing a function defers execution
    // to mount instead of every render. Defaults to '' (All) to match the
    // page's actual unfiltered initial fetch. If the URL deep-links a
    // fromDate/toDate pair, we either map it to a preset (e.g. exactly 7 days
    // ending today → '7d') or fall through to 'custom' and seed the date
    // pickers.
    const [periodState, setPeriodState] = useState<PeriodState>(buildInitialPeriod);
    const [appliedPeriod, setAppliedPeriod] = useState<PeriodState>(buildInitialPeriod);
    const { period: timePeriod, start: customStartDate, end: customEndDate } = periodState;

    const setTimePeriod = useCallback((next: string) => {
        if (!VALID_TIME_PERIODS.has(next)) return;
        setPeriodState((prev) => {
            // Switching away from 'custom' clears the explicit date pickers —
            // the preset takes over. Switching TO 'custom' leaves whatever was
            // there so the user can edit a previously-loaded range.
            if (next !== 'custom') return { period: next, start: null, end: null };
            return { ...prev, period: next };
        });
    }, []);

    const setCustomStartDate = useCallback((d: Dayjs | null) => {
        setPeriodState((prev) => ({ ...prev, start: d }));
    }, []);
    const setCustomEndDate = useCallback((d: Dayjs | null) => {
        setPeriodState((prev) => ({ ...prev, end: d }));
    }, []);

    // Re-sync the dropdown values + date range when URL params change. Mirrors
    // the parent alert-list's URL-as-source-of-truth effect.
    const urlAlertType = searchParams.get('alertType');
    const urlStatus = searchParams.get('status');
    const urlFromDate = searchParams.get('fromDate');
    const urlToDate = searchParams.get('toDate');
    useEffect(() => {
        const nextValuesPatch: Partial<Values> = {
            alertType: urlAlertType && VALID_ALERT_TYPES.has(urlAlertType) ? urlAlertType : '',
            status: urlStatus && VALID_ALERT_STATUSES.has(urlStatus) ? urlStatus : '',
        };
        setValues((prev) => ({ ...prev, ...nextValuesPatch }));
        setApplied((prev) => ({ ...prev, ...nextValuesPatch }));
        if (urlFromDate && urlToDate && ISO_DATE_RE.test(urlFromDate) && ISO_DATE_RE.test(urlToDate)) {
            const next: PeriodState = {
                period: rangeToPeriod(urlFromDate, urlToDate),
                start: dayjs(urlFromDate),
                end: dayjs(urlToDate),
            };
            setPeriodState(next);
            setAppliedPeriod(next);
        }
    }, [urlAlertType, urlStatus, urlFromDate, urlToDate]);

    // Primary: Status, Alert Type, Search by name — always visible
    const primaryFilters: FilterField[] = useMemo(() => [
        { key: 'status', label: 'Status', type: 'select', options: ALERT_STATUSES },
        { key: 'alertType', label: 'Alert Type', type: 'select', options: ALERT_TYPES },
        { key: 'initiatedBy', label: 'User Name', type: 'text', placeholder: 'Search by name...' },
    ], []);

    // When groups are selected, narrow the visible team list to only those
    // teams that belong to the selected group(s). Mirrors the POM Users
    // filter so the two pages feel identical.
    const visibleTeams = useMemo(() => {
        if (values.groupIds.length === 0) return allTeams;
        const groupSet = new Set(values.groupIds);
        return allTeams.filter((t) => t.groupId && groupSet.has(t.groupId));
    }, [allTeams, values.groupIds]);

    // Map team id → its parent group's name so the Team filter dropdown can
    // be sectioned (disambiguates Molina-style duplicate team names across
    // multiple groups).
    const groupNameByTeamId = useMemo(() => {
        const m = new Map<string, string>();
        const groupNameById = new Map<string, string>();
        for (const g of allGroups) groupNameById.set(g.id || (g as any)._id || '', g.name || '');
        for (const t of allTeams) {
            const tid = t.id || (t as any)._id || '';
            const gid = t.groupId;
            if (tid && gid && groupNameById.has(gid)) {
                m.set(tid, groupNameById.get(gid) as string);
            }
        }
        return m;
    }, [allTeams, allGroups]);

    // Expanded: Group, Team, Tags — behind "+ Filters"
    const expandedFilters: FilterField[] = useMemo(() => [
        {
            key: 'groupIds',
            label: 'Group',
            type: 'grouped-multiselect',
            placeholder: 'All groups',
            options: allGroups.map((g) => ({
                label: g.name || '',
                value: g.id || (g as any)._id || '',
            })),
        },
        {
            key: 'teamIds',
            label: 'Team',
            type: 'grouped-multiselect',
            // When a group is selected the list is already narrowed, so the
            // group subheader adds noise — omit `group` in that case.
            placeholder: values.groupIds.length > 0 ? 'Teams in selected group(s)' : 'All teams',
            options: visibleTeams.map((t) => {
                const tid = t.id || (t as any)._id || '';
                return {
                    label: t.name || '',
                    value: tid,
                    group: values.groupIds.length === 0 ? groupNameByTeamId.get(tid) : undefined,
                };
            }),
        },
        {
            key: 'tags', label: 'Tags', type: 'multiselect',
            options: allTags.map((t) => ({ label: t.name || '', value: t._id || '' })),
        },
        // TM-8: test alerts are hidden by default server-side; this control
        // opts into showing them (or only them). '' = the default, so it
        // doesn't count toward the More Filters badge. Gated on the Test Mode
        // feature flag: the flag only resolves true from an API version that
        // also validates/honors the `testData` param, so the control can never
        // send a param the backend would silently ignore.
        ...(showTestMode ? [{
            key: 'testData', label: 'Test Data', type: 'select' as const,
            defaultValue: '', options: TEST_DATA_OPTIONS,
        }] : []),
    ], [allGroups, visibleTeams, allTags, groupNameByTeamId, values.groupIds, showTestMode]);

    const handleChange = useCallback((key: string, value: any) => {
        setValues((prev) => {
            const next = { ...prev, [key]: value };
            // When the group filter changes, drop any selected teams that are
            // no longer in the narrowed team list so stale chips don't linger.
            if (key === 'groupIds') {
                const selectedGroups = value as string[];
                if (selectedGroups.length > 0) {
                    const groupSet = new Set(selectedGroups);
                    const visibleTeamIds = new Set(
                        allTeams
                            .filter((t) => t.groupId && groupSet.has(t.groupId))
                            .map((t) => t.id || (t as any)._id || '')
                    );
                    next.teamIds = prev.teamIds.filter((tid) => visibleTeamIds.has(tid));
                }
                // Groups cleared → leave existing teamIds intact (they remain valid)
            }
            return next;
        });
    }, [allTeams]);

    // Build the API payload from a (values, period) pair and dispatch it.
    // Shared between handleApply, pill removal, and clear so the request
    // shape stays consistent.
    const dispatchFilter = useCallback((nextValues: Values, nextPeriod: PeriodState) => {
        const payload = { ...nextValues } as unknown as AlertTypes.Filters;
        // '' means "server default" — the backend's testData enum rejects an
        // empty string, so omit the key entirely.
        if (!nextValues.testData) delete payload.testData;
        const range = resolveDateRange(nextPeriod);
        if (range) {
            payload.fromDate = range.fromDate;
            payload.toDate = range.toDate;
        }
        onFilter(payload).catch((err) => {
            console.error('[AlertListFilters] onFilter failed', err);
        });
    }, [onFilter]);

    const handleApply = useCallback(() => {
        // Validate custom range before firing the request — without both dates
        // resolveDateRange would silently return null and the user would get an
        // unbounded result set, which they didn't ask for.
        if (periodState.period === 'custom' && (!periodState.start || !periodState.end)) {
            toast('Pick a start and end date for the custom range.', { type: 'warning', autoClose: 2500 });
            return;
        }
        setApplied(values);
        setAppliedPeriod(periodState);
        dispatchFilter(values, periodState);
    }, [values, periodState, dispatchFilter]);

    const handleClear = useCallback(() => {
        setValues(EMPTY_VALUES);
        setApplied(EMPTY_VALUES);
        setPeriodState(EMPTY_PERIOD);
        setAppliedPeriod(EMPTY_PERIOD);
        dispatchFilter(EMPTY_VALUES, EMPTY_PERIOD);
    }, [dispatchFilter]);

    const removeAppliedValues = useCallback((next: Values) => {
        setValues(next);
        setApplied(next);
        dispatchFilter(next, appliedPeriod);
    }, [appliedPeriod, dispatchFilter]);

    const removeAppliedPeriod = useCallback(() => {
        setPeriodState(EMPTY_PERIOD);
        setAppliedPeriod(EMPTY_PERIOD);
        dispatchFilter(applied, EMPTY_PERIOD);
    }, [applied, dispatchFilter]);

    const groupNameById = useMemo(() => {
        const m = new Map<string, string>();
        for (const g of allGroups) m.set(g.id || (g as any)._id || '', g.name || '');
        return m;
    }, [allGroups]);
    // Team names come from both stores: GroupService.fetchAll overwrites the
    // teams slice with only its group-nested teams, so whichever fetch landed
    // last decides what `allTeams` holds. Reading group-nested teams too makes
    // the pill label independent of that race.
    const teamNameById = useMemo(() => {
        const m = new Map<string, string>();
        const add = (t: any) => {
            const id = t?.id || t?._id || t?.mongoId;
            // Gate on the name, not the key: a nameless entry in the first source
            // must not block a named copy of the same team in the second.
            if (id && t?.name && !m.get(id)) m.set(id, t.name);
        };
        for (const t of allTeams) add(t);
        for (const g of allGroups) for (const t of (g.teams || [])) add(t);
        return m;
    }, [allTeams, allGroups]);
    const tagNameById = useMemo(() => {
        const m = new Map<string, string>();
        for (const t of allTags) m.set(t._id || '', t.name);
        return m;
    }, [allTags]);

    const periodPillLabel = (p: PeriodState): string | null => {
        if (!p.period) return null;
        if (p.period === 'custom') {
            if (!p.start || !p.end) return null;
            const sameYear = p.start.year() === p.end.year();
            const startFmt = sameYear ? p.start.format('MMM D') : p.start.format('MMM D, YYYY');
            return `${startFmt} – ${p.end.format('MMM D, YYYY')}`;
        }
        return TIME_PERIOD_LABELS[p.period] ?? p.period;
    };

    const appliedFilters = useMemo(() => {
        const pills: { key: string; label: string; onRemove: () => void }[] = [];
        if (applied.status) pills.push({
            key: 'status',
            label: `Status: ${ALERT_STATUS_LABELS[applied.status] ?? applied.status}`,
            onRemove: () => removeAppliedValues({ ...applied, status: '' }),
        });
        if (applied.alertType) pills.push({
            key: 'alertType',
            label: `Type: ${ALERT_TYPE_LABELS[applied.alertType] ?? applied.alertType}`,
            onRemove: () => removeAppliedValues({ ...applied, alertType: '' }),
        });
        if (applied.initiatedBy) pills.push({
            key: 'initiatedBy',
            label: `Name: ${applied.initiatedBy}`,
            onRemove: () => removeAppliedValues({ ...applied, initiatedBy: '' }),
        });
        if (applied.testData) pills.push({
            key: 'testData',
            label: `Test data: ${TEST_DATA_LABELS[applied.testData] ?? applied.testData}`,
            onRemove: () => removeAppliedValues({ ...applied, testData: '' }),
        });
        for (const groupId of applied.groupIds) {
            const name = displayName(groupNameById.get(groupId));
            pills.push({
                key: `group:${groupId}`,
                label: `Group: ${name}`,
                onRemove: () => removeAppliedValues({ ...applied, groupIds: applied.groupIds.filter((g) => g !== groupId) }),
            });
        }
        for (const teamId of applied.teamIds) {
            const name = displayName(teamNameById.get(teamId));
            pills.push({
                key: `team:${teamId}`,
                label: `Team: ${name}`,
                onRemove: () => removeAppliedValues({ ...applied, teamIds: applied.teamIds.filter((t) => t !== teamId) }),
            });
        }
        for (const tagId of applied.tags) {
            const name = displayName(tagNameById.get(tagId));
            pills.push({
                key: `tag:${tagId}`,
                label: `Tag: ${name}`,
                onRemove: () => removeAppliedValues({ ...applied, tags: applied.tags.filter((t) => t !== tagId) }),
            });
        }
        const pLabel = periodPillLabel(appliedPeriod);
        if (pLabel) {
            pills.push({
                key: 'time-period',
                label: `Time: ${pLabel}`,
                onRemove: () => removeAppliedPeriod(),
            });
        }
        return pills;
    }, [applied, appliedPeriod, groupNameById, teamNameById, tagNameById, removeAppliedValues, removeAppliedPeriod]);

    return (
        <UnifiedFilterBar
            primaryFilters={primaryFilters}
            expandedFilters={expandedFilters}
            values={values}
            appliedFilters={appliedFilters}
            onChange={handleChange}
            onApply={handleApply}
            onClear={handleClear}
            onExport={onDownload}
            showTimePeriod
            timePeriod={timePeriod}
            onTimePeriodChange={setTimePeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomStartDateChange={setCustomStartDate}
            onCustomEndDateChange={setCustomEndDate}
        />
    );
};
